import * as React from "react";
import { Link } from "react-router-dom";
import { Form, useFormApi } from "informed";
import { MultiSelect } from "../forms/informedv2/MultiSelect";
import { useReferenceContextProvider } from "../Core/ReferenceContextProvider";
import { useSecurityContext, UserContext } from "../Core/SecurityContext";
import { AuditForm, AuditModal } from "../auditmodal/components/AuditModal";
import { ActionButtons } from "../misc/ActionButtons";
import { TimeZone } from "webportalclient";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { User } from "../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";

interface AccountSettingsFormState {
    selectedTimeZone: TimeZone;
}

interface AccountSettingsFormProps {
    context: UserContext;
    currentUser: User;
}

export const AccountSettingsForm = ({ context, currentUser }: AccountSettingsFormProps) => {
    const { timeZones } = useReferenceContextProvider();
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const client = useWebPortalApiClient();
    const [submitting, setSubmitting] = React.useState(false);
    const formApi = useFormApi<AccountSettingsFormState>();
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [successAlertShown, setSuccessAlertShown] = React.useState(false);

    const onPreAuditSubmit = () => {
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        formApi.validate();
        const state = formApi.getState();

        try {
            await client.client.patchUser({
                timeZoneId: state.values.selectedTimeZone.id,
            }, currentUser.Id, auditForm.Annotation);

            setSuccessAlertShown(true);
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setSubmitting(false);
        setAuditModalVisible(false);
    }

    return (
        <>
            <Alert show={successAlertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/`}>Home</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">Account Settings</h2>
                </div>
            </div>
            <div className="panel panel-default">
                <div className="panel-body">
                    <div className="row">
                        <div className="form-group col-sm-12">
                            <label>Preferred Time Zone</label>
                            <MultiSelect
                                id="selectedTimeZone"
                                field="selectedTimeZone"
                                options={timeZones}
                                labelKey="name"
                                valueKey="legacyId"
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="h2 pull-right">
                                <button className="btn btn-primary" type="submit" disabled={submitting} onClick={onPreAuditSubmit}>
                                    Save
                                </button>&nbsp;
                                <a className="btn btn-primary btn-outline" href={context.PasswordResetUrl} target="_blank">Change password</a>&nbsp;
                                <a className="btn btn-primary btn-outline" href={`${context.ProfileEditUrl}?redirect=/account/signin`} target="_blank">Update profile</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const AccountSettings = () => {
    const legacyClient = useWebPortalLegacyClient();
    const { context } = useSecurityContext();
    const [initialValues, setInitialValues] = React.useState<{ selectedTimeZone: TimeZone }>();
    const { timeZones } = useReferenceContextProvider();
    const [currentUser, setCurrentUser] = React.useState();

    React.useEffect(() => {
        const getCurrentUser = async () => {
            const userResult = await legacyClient.getCurrentUser();
            setCurrentUser(userResult);
            setInitialValues({ selectedTimeZone: timeZones.find(tz => tz.id === userResult.TimeZoneId) });
        }

        getCurrentUser();
    }, []);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading Account Settings..." />
    }

    return (
        <Form<AccountSettingsFormState> initialValues={initialValues}>
            <AccountSettingsForm context={context} currentUser={currentUser} />
        </Form>
    )
}
